/**
 * Periods on the scoreboard screens — one vocabulary for the chips, the URL and the API query, so
 * "Week" on Team today, on a rep page and on My week is literally the same request (one language).
 *
 * The server owns the calendar (tenant timezone, week start, working days, E7); the client only
 * names a period. CUSTOM carries `from` / `to` (yyyy-MM-dd, tenant dates, ≤ 400 days).
 */
import type { ScoreboardPeriod } from './types'

export interface PeriodSelection {
  period: ScoreboardPeriod
  from?: string | null
  to?: string | null
}

export const PERIODS: ScoreboardPeriod[] = [
  'TODAY', 'YESTERDAY', 'WEEK', 'LAST_WEEK', 'MONTH', 'LAST_MONTH', 'QUARTER', 'LAST_QUARTER', 'CUSTOM',
]

export const PERIOD_LABEL: Record<ScoreboardPeriod, string> = {
  TODAY: 'Today',
  YESTERDAY: 'Yesterday',
  WEEK: 'Week',
  LAST_WEEK: 'Last week',
  MONTH: 'Month',
  LAST_MONTH: 'Last month',
  QUARTER: 'Quarter',
  LAST_QUARTER: 'Last quarter',
  CUSTOM: 'Custom',
}

/** The summary's chips (P2.1) and the full table's (P2.2). */
export const SUMMARY_CHIPS: ScoreboardPeriod[] = ['TODAY', 'WEEK', 'MONTH', 'QUARTER']
export const TABLE_CHIPS: ScoreboardPeriod[] = ['TODAY', 'YESTERDAY', 'WEEK', 'LAST_WEEK', 'MONTH', 'QUARTER', 'CUSTOM']
/** A rep's own table (P2.5) and a rep page (P2.3). */
export const REP_CHIPS: ScoreboardPeriod[] = ['WEEK', 'LAST_WEEK', 'MONTH', 'QUARTER']

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
export const MAX_CUSTOM_DAYS = 400

function isPeriod(v: string): v is ScoreboardPeriod {
  return (PERIODS as string[]).includes(v)
}

function dayNumber(iso: string): number {
  const [y, m, dd] = iso.split('-').map(Number)
  return Date.UTC(y, m - 1, dd) / 86_400_000
}

/** A custom range the server will accept: two dates, in order, at most 400 days apart. */
export function validCustomRange(from?: string | null, to?: string | null): boolean {
  if (!from || !to || !ISO_DATE.test(from) || !ISO_DATE.test(to)) return false
  const span = dayNumber(to) - dayNumber(from)
  return span >= 0 && span <= MAX_CUSTOM_DAYS
}

/**
 * Read the period from a URL. Anything unknown — or a CUSTOM without a valid range — falls back
 * to `fallback`, so a hand-edited link never produces a 400.
 */
export function parsePeriod(params: URLSearchParams, fallback: ScoreboardPeriod = 'TODAY'): PeriodSelection {
  const raw = (params.get('period') ?? '').toUpperCase()
  if (!isPeriod(raw)) return { period: fallback }
  if (raw === 'CUSTOM') {
    const from = params.get('from')
    const to = params.get('to')
    return validCustomRange(from, to) ? { period: 'CUSTOM', from, to } : { period: fallback }
  }
  return { period: raw }
}

/** The URL updates for a selection (`null` deletes the key). */
export function periodParamUpdate(sel: PeriodSelection): Record<string, string | null> {
  if (sel.period === 'CUSTOM' && validCustomRange(sel.from, sel.to)) {
    return { period: 'CUSTOM', from: sel.from ?? null, to: sel.to ?? null }
  }
  return { period: sel.period === 'CUSTOM' ? null : sel.period, from: null, to: null }
}

/** The API query string for a selection (plus any extra keys), stable in key order. */
export function periodQuery(sel: PeriodSelection, extra: Record<string, string | null | undefined> = {}): string {
  const q = new URLSearchParams()
  if (sel.period === 'CUSTOM' && validCustomRange(sel.from, sel.to)) {
    q.set('period', 'CUSTOM')
    q.set('from', sel.from as string)
    q.set('to', sel.to as string)
  } else {
    q.set('period', sel.period === 'CUSTOM' ? 'TODAY' : sel.period)
  }
  for (const [k, v] of Object.entries(extra)) if (v != null && v !== '') q.set(k, v)
  return q.toString()
}

/**
 * Rows of a rep's table: by day for anything up to two weeks, by week beyond that (My week's
 * "Switching to Month shows one row per week").
 */
export function groupByFor(sel: PeriodSelection): 'day' | 'week' {
  switch (sel.period) {
    case 'TODAY':
    case 'YESTERDAY':
    case 'WEEK':
    case 'LAST_WEEK':
      return 'day'
    case 'CUSTOM':
      return validCustomRange(sel.from, sel.to) && dayNumber(sel.to as string) - dayNumber(sel.from as string) <= 13
        ? 'day'
        : 'week'
    default:
      return 'week'
  }
}

/** "Today", "Week", or "1 Oct – 5 Oct" for a custom range. */
export function periodTitle(sel: PeriodSelection): string {
  if (sel.period === 'CUSTOM' && sel.from && sel.to) return sel.from === sel.to ? shortDate(sel.from) : `${shortDate(sel.from)} – ${shortDate(sel.to)}`
  return PERIOD_LABEL[sel.period]
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** `2026-10-05` → `5 Oct` — read as a calendar date, never shifted by the browser's zone. */
export function shortDate(iso: string): string {
  if (!ISO_DATE.test(iso)) return iso
  const [, m, dd] = iso.split('-').map(Number)
  return `${dd} ${MONTHS[m - 1]}`
}

/** `2026-10-05` → `Mon 5 Oct`. */
export function dayLabel(iso: string): string {
  if (!ISO_DATE.test(iso)) return iso
  const [y, m, dd] = iso.split('-').map(Number)
  const wd = new Date(Date.UTC(y, m - 1, dd)).getUTCDay()
  return `${WEEKDAYS[wd]} ${dd} ${MONTHS[m - 1]}`
}

/** A slice's row label: a day, or a week range. */
export function sliceLabel(from: string, to: string): string {
  return from === to ? dayLabel(from) : `${shortDate(from)} – ${shortDate(to)}`
}
